import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { DeleteSessionButton } from "../components/DeleteSessionButton";
import { api } from "../lib/api";
import { formatBytes, formatDate, formatDuration } from "../lib/format";
import type { SessionStatus } from "../lib/types";

const STATUS_STYLES: Record<SessionStatus, string> = {
  awaiting_upload: "bg-zinc-800 text-zinc-400",
  uploaded: "bg-sky-500/10 text-sky-400",
  processing: "bg-sky-500/10 text-sky-400",
  ready: "bg-emerald-500/10 text-emerald-400",
  failed: "bg-red-500/10 text-red-400",
};

export function SessionsPage() {
  const { t } = useTranslation();
  const sessions = useQuery({
    queryKey: ["sessions"],
    queryFn: api.listSessions,
    // Keep the list moving while something is still being ingested.
    refetchInterval: (query) =>
      query.state.data?.some(
        (session) => session.status === "uploaded" || session.status === "processing",
      )
        ? 2_000
        : false,
  });

  if (sessions.isPending) {
    return <p className="text-sm text-zinc-500">{t("sessions.loading")}</p>;
  }

  if (sessions.isError) {
    return <p className="text-sm text-red-400">{t("sessions.error")}</p>;
  }

  if (sessions.data.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-800 px-6 py-16 text-center">
        <h2 className="text-lg font-medium">{t("sessions.emptyTitle")}</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-zinc-400">
          {t("sessions.emptyBody")}
        </p>
        <Link
          to="/upload"
          className="mt-6 inline-block rounded-lg bg-sky-500 px-4 py-2 text-sm font-medium text-zinc-950 transition-colors hover:bg-sky-400"
        >
          {t("sessions.emptyAction")}
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("sessions.title")}
        </h1>
        <Link
          to="/upload"
          className="rounded-lg bg-sky-500 px-3 py-1.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-sky-400"
        >
          {t("sessions.upload")}
        </Link>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-4 py-3 font-medium">{t("sessions.columns.session")}</th>
              <th className="px-4 py-3 font-medium">{t("sessions.columns.track")}</th>
              <th className="px-4 py-3 font-medium">{t("sessions.columns.car")}</th>
              <th className="px-4 py-3 text-right font-medium">
                {t("sessions.columns.laps")}
              </th>
              <th className="px-4 py-3 text-right font-medium">
                {t("sessions.columns.duration")}
              </th>
              <th className="px-4 py-3 text-right font-medium">
                {t("sessions.columns.stored")}
              </th>
              <th className="px-4 py-3 font-medium">{t("sessions.columns.status")}</th>
              <th className="px-4 py-3">
                <span className="sr-only">{t("sessions.columns.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {sessions.data.map((session) => (
              <tr key={session.id} className="hover:bg-zinc-900/40">
                <td className="px-4 py-3">
                  <Link to={`/sessions/${session.id}`} className="hover:text-sky-400">
                    <span className="block font-medium">{session.original_filename}</span>
                    <span className="text-xs text-zinc-500">
                      {formatDate(session.recorded_at ?? session.created_at)}
                    </span>
                  </Link>
                </td>
                <td className="px-4 py-3 text-zinc-300">{session.track_name ?? "—"}</td>
                <td className="px-4 py-3 text-zinc-300">{session.car_name ?? "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums">{session.lap_count}</td>
                <td className="px-4 py-3 text-right tabular-nums text-zinc-400">
                  {formatDuration(session.duration_ms)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-zinc-400">
                  {formatBytes(session.trace_bytes)}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[session.status]}`}
                  >
                    {t(`status.${session.status}`)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <DeleteSessionButton sessionId={session.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
